import 'dayjs/plugin/timezone';
import 'dayjs/plugin/utc';
import dayjs, { type Dayjs } from 'dayjs';

/**
 * Normalize a JSON date string for dayjs parsing.
 * - Converts non-standard offsets like +HHMM to +HH:MM
 * - Appends 'Z' to strings without timezone info (matching date-fns parseJSON behavior)
 */
function normalizeOffset(timeString: string): string {
  if (typeof timeString !== 'string') return String(timeString ?? '');
  // Normalize ' UTC' suffix to 'Z'
  let normalized = timeString.replace(/\s*UTC$/i, 'Z');
  // Normalize +HHMM → +HH:MM (without colon, at end of string)
  normalized = normalized.replace(/([+-])(\d{2})(\d{2})$/, '$1$2:$3');
  // If no timezone info (no Z, no +/- offset after time), treat as UTC
  if (!/[Zz]$/.test(normalized) && !/[+-]\d{2}:\d{2}$/.test(normalized)) {
    normalized += 'Z';
  }
  return normalized;
}

/**
 * Convert time string from a JSON response to a given format.
 * i.e., reformatJSONTimeString('2000-03-15T05:20:10.123Z', 'L, LT') = '03/15/2000, 12:20 AM'
 * @param timeString
 *   - The original time string from JSON response.
 *   - The following original timeString formats are supported:
 *     2000-03-15T05:20:10.123Z: The output of .toISOString() and JSON.stringify(new Date())
 *     2000-03-15T05:20:10Z: Without milliseconds
 *     2000-03-15T05:20:10+00:00: With a zero offset, the default JSON encoded format in some other languages
 *     2000-03-15T05:20:10+05:45: With a positive or negative offset, the default JSON encoded format in some other languages
 *     2000-03-15T05:20:10+0000: With a zero offset without a colon
 *     2000-03-15T05:20:10: Without a trailing 'Z' symbol
 *     2000-03-15T05:20:10.1234567: Up to 7 digits in milliseconds field. Only first 3 are taken into account since JS does not allow fractional milliseconds
 *     2000-03-15 05:20:10: With a space instead of a 'T' separator for APIs returning a SQL date without reformatting
 * @param timeFormat
 *   - dayjs format string. See: https://day.js.org/docs/en/display/format
 * @param timeZoneName (Optional)
 *   - Name of the target output time zone (i.e., 'America/Halifax')
 *   - If timezone is not provided, will reformatting based on browser default timezone.
 * @returns
 *   - Reformatted time string according to the time code
 *   - i.e., reformatJSONTimeString('2000-03-15T05:20:10.123Z', 'L, LT') = '03/15/2000, 12:20 AM'
 */
export function reformatJSONTimeString(
  timeString: string,
  timeFormat: string,
  timeZoneName?: string,
): string {
  const normalized = normalizeOffset(timeString);
  let timeObj = dayjs(normalized);
  if (timeZoneName) {
    timeObj = timeObj.tz(timeZoneName);
  }
  return timeObj.format(timeFormat);
}

/**
 * Get a merged time range string.
 * @param startTimeString - Start timestamp in JSON format.
 * @param endTimeString - End timestamp in JSON format.
 * @param timeZoneName - Name of the target output time zone. i.e., 'America/Halifax'
 * @returns i.e., '10:00 - 11:30 AM'
 */
export function getMergedTimeRange(
  startTimeString: string,
  endTimeString: string,
  timeZoneName?: string,
): string {
  let formattedStart = reformatJSONTimeString(startTimeString, 'h:mm a', timeZoneName);
  const formattedEnd = reformatJSONTimeString(endTimeString, 'h:mm a', timeZoneName);
  // Merge common AM/PM if start and end has the same value
  if (formattedStart.split(' ')[1] === formattedEnd.split(' ')[1]) {
    [formattedStart] = formattedStart.split(' ');
  }
  return `${formattedStart} - ${formattedEnd}`;
}

/**
 * Get a string reflecting the diff between now and a given timestamp.
 * @param startTimeString - Start timestamp in JSON format.
 * @param endTimeString - Start timestamp in JSON format.
 * @returns i.e., 'Just Now, 15m, 2h, 6d, etc.'
 */
export function getTimeDiffString(startTimeString: string, endTimeString: string = ''): string {
  const startTime = dayjs(startTimeString);
  const endTime = endTimeString ? dayjs(endTimeString) : dayjs();
  const diffInMin = endTime.diff(startTime, 'minute');
  if (diffInMin <= 1) {
    return 'Just Now';
  }
  if (diffInMin <= 60) {
    return `${diffInMin}m`;
  }
  if (diffInMin <= 24 * 60) {
    return `${Math.round(diffInMin / 60)}h`;
  }
  const diffInDay = endTime.startOf('day').diff(startTime.startOf('day'), 'day');
  if (diffInDay <= 7) {
    return `${diffInDay}d`;
  }
  return startTime.format('ll');
}

/**
 * Format a date in a specific timezone using dayjs.
 * Replacement for date-fns-tz formatInTimeZone.
 * @param timeZone - IANA timezone name
 * @param fmt - dayjs format string
 */
export function formatInTimeZone(date: Date | string, timeZone: string, fmt: string): string {
  return dayjs(date).tz(timeZone).format(fmt);
}

/**
 * Convert a UTC date to the equivalent zoned time representation.
 * Returns a dayjs object in the specified timezone.
 * Replacement for date-fns-tz utcToZonedTime.
 * @param timeZone - IANA timezone name
 */
export function utcToZonedTime(date: Date | string, timeZone: string): Dayjs {
  return dayjs(date).tz(timeZone);
}

/**
 * Convert a zoned time to UTC.
 * Interprets the given date as being in the specified timezone and returns the UTC equivalent.
 * Replacement for date-fns-tz zonedTimeToUtc.
 * @param timeZone - IANA timezone name
 */
export function zonedTimeToUtc(date: Date | string, timeZone: string): Date {
  // Format as YYYY-MM-DDTHH:mm:ss to strip any existing timezone info,
  // so dayjs.tz interprets it as a local time in the given timezone.
  const d = dayjs(date);
  if (!d.isValid()) return new Date(NaN);
  const dateStr = d.format('YYYY-MM-DDTHH:mm:ss');
  return dayjs.tz(dateStr, timeZone).utc().toDate();
}
